import { parseInviteCode, type RunListing } from '@d2/schema';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { type RunDirectory, type RunRefusalReason } from '../game/runs';
import { LobbyPanel } from './lobby-panel';
import { startJoin } from './lobby-model';

/**
 * The lobby's counter: the one thing in town that talks to the **game** server.
 *
 * `lobby-panel.tsx` draws and decides nothing; `lobby-model.ts` owns the words
 * and the latch; `game/runs.ts` owns the two requests. What is left — a read on
 * arrival, a promise that may land after the player has gone, and one join at a
 * time — is genuinely React's job, and it lives here rather than in
 * `AccountGate.tsx` for the reason `VendorCounter` gives: **this component
 * exists exactly as long as town is on screen**. Descending unmounts it and
 * coming back mounts a new one, so a player who has just finished a run sees a
 * directory read afresh without anybody having to remember to invalidate one.
 *
 * ## Both ways in end in the same three steps
 *
 * A row that was clicked and a code that was typed are the same journey, and
 * the only difference is where the eight symbols came from:
 *
 *   1. **parse it here**, with `@d2/schema`'s own `parseInviteCode` — so a typo
 *      is answered instantly and costs no request, and so the code that goes out
 *      is the normalised one a server will recognise;
 *   2. **resolve it**, `GET /runs/:code`, which is where the party's fullness is
 *      checked — *now*, not when the list was drawn;
 *   3. **hand the address up**, and let the shell open the socket.
 *
 * The listing deliberately carries no room id (`@d2/schema`'s `RunListing`), so
 * step 2 is not an optimisation anybody could skip: there is nothing cached to
 * skip it *with*. That is the design — a room id in a list is a permanent
 * standing invitation to a room whose party may since have filled, and a join
 * against a locked room is answered 522, which this client used to report as an
 * outage (`game/join-refusal.ts`).
 *
 * ## There is no push channel and this does not invent one
 *
 * No polling loop, no socket, no timer. The directory is read on mount and
 * whenever the player asks for it again — because a lobby that refreshed itself
 * every few seconds would be a client hammering the game process for a list
 * that is empty almost always, and one that moved rows out from under a
 * pointer.
 *
 * ## One join at a time, latched twice
 *
 * `joining` is state, because the panel draws from it. The **ref** beside it is
 * the latch that actually protects the join: state is batched, and two clicks
 * inside one flush would both read the same `null`. Two joins in flight are two
 * seat reservations for one character, which the room answers 409 — a refusal
 * about this client's own doing, shown to a player who merely pressed twice.
 */

export interface LobbyBoardProps {
  readonly runs: RunDirectory;
  readonly token: string;
  /**
   * The room this player is joining, as the directory just answered it.
   *
   * A room id and not a code, because that is what `joinById` takes — and it is
   * the one thing on this seam that could not have been known a moment earlier.
   * The shell opens the socket; this component never does.
   */
  readonly onJoin: (roomId: string) => void;
  /** The token is no longer accepted. The shell owns what happens next. */
  readonly onSessionEnded: () => void;
}

export const LobbyBoard = ({ runs, token, onJoin, onSessionEnded }: LobbyBoardProps): ReactElement => {
  const [listing, setListing] = useState<readonly RunListing[] | null>(null);
  const [failure, setFailure] = useState<RunRefusalReason | null>(null);
  const [reading, setReading] = useState(false);
  const [joining, setJoining] = useState<string | null>(null);
  const [note, setNote] = useState<RunRefusalReason | null>(null);

  /** True while this component is mounted. An answer may land after a descent. */
  const live = useRef(true);
  /** The latch that actually stops the second join. See the header for why the state is not it. */
  const inFlight = useRef<string | null>(null);
  /**
   * Whether a board has ever been read.
   *
   * It decides *where* a refusal is said, which is a different question from
   * what it says: with no board yet the reason stands in for one, and with a
   * board already on screen it is said in the live region beside it. A ref
   * rather than the state itself because the answer that refuses is landing in
   * a promise continuation, where `listing` is whatever it was when the request
   * went out.
   */
  const everRead = useRef(false);

  useEffect(() => {
    live.current = true;

    return (): void => {
      live.current = false;
    };
  }, []);

  /**
   * A refusal, routed.
   *
   * A refused token is not a lobby problem and must not be drawn as one — it is
   * the session ending, which is the shell's business. Everything else is
   * something to say on the board, and four of the five are a healthy server
   * answering precisely.
   */
  const refused = useCallback(
    (refusal: RunRefusalReason, say: (refusal: RunRefusalReason) => void): void => {
      if (refusal === 'unauthorised') {
        onSessionEnded();

        return;
      }

      say(refusal);
    },
    [onSessionEnded],
  );

  const read = useCallback((): void => {
    setReading(true);

    void (async (): Promise<void> => {
      const answer = await runs.listRuns(token);
      if (!live.current) return;

      setReading(false);

      if (!answer.ok) {
        // Said once, in the place that has room for it. A refused **refresh**
        // keeps the runs it had, because a directory that stopped answering is
        // not a claim that those runs ended — and blanking the list would take
        // away the codes the player can still read off it.
        refused(answer.refusal, (refusal) => {
          if (everRead.current) setNote(refusal);
          else setFailure(refusal);
        });

        return;
      }

      everRead.current = true;
      setListing(answer.value);
      setFailure(null);
      setNote(null);
    })();
  }, [refused, runs, token]);

  // Once per visit to town — which is once per mount, because this component
  // only exists while town is on screen. Everything after that is the player
  // asking.
  useEffect(() => {
    read();
  }, [read]);

  /**
   * One join, from the press to the socket somebody else opens.
   *
   * The code is parsed **before** anything goes out. That is not a second
   * opinion about what a code is: `parseInviteCode` is the same function the
   * route parses its path segment with, so this is one rule called on the near
   * side of the network, which is the only place it can be instant.
   */
  const join = useCallback(
    (typed: string): void => {
      const code = parseInviteCode(typed);
      if (code === null) {
        setNote('malformed-code');

        return;
      }

      const started = startJoin(inFlight.current, code);
      // A second press while one is in flight. What this protects is the room's
      // one-seat-per-character rule, and therefore the player from a refusal
      // their own client caused.
      if (started === null) return;

      inFlight.current = started;
      setJoining(started);
      setNote(null);

      void (async (): Promise<void> => {
        const answer = await runs.addressOf(token, started);
        inFlight.current = null;
        if (!live.current) return;

        setJoining(null);

        if (!answer.ok) {
          refused(answer.refusal, setNote);

          return;
        }

        onJoin(answer.value);
      })();
    },
    [onJoin, refused, runs, token],
  );

  return (
    <LobbyPanel
      runs={listing}
      failure={failure}
      onRefresh={read}
      onJoin={join}
      joining={joining}
      note={note}
      reading={reading}
    />
  );
};
